import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../../services/notification.service';
import {
  DropdownOption,
  SimpleDropdownComponent,
} from '../../../../shared/components/simple-dropdown/simple-dropdown.component';
import { EmptyResultComponent } from '../../../../shared/components/empty-result/empty-result.component';
import { CompanyContextService } from '../../../services/company-context.service';
import {
  AdminLocationRow,
  LocationSettingsService,
} from '../../../services/location-settings.service';
import {
  LOCATION_SETTINGS_TABS,
  LocationSettingsTab,
} from '../../../constants/location-settings.constants';
import {
  buildLocationSettingsQueryParams,
  parseLocationSettingsTab,
} from '../../../utils/location-settings.util';
import { LocationLanesComponent } from './lanes/location-lanes.component';
import { LocationTunnelComponent } from './tunnel/location-tunnel.component';
import { LocationWayComponent } from './way/location-way.component';

/**
 * Company detail → Location tab (CWS-1921; was the standalone "Location Settings" page).
 *
 * The company comes from the detail shell; this tab owns the location pick and the
 * Lanes | Tunnel | Way sub-tabs, both mirrored in the URL (`?location=&tab=`). The
 * three panels stay mounted so their counts are known up front and unsaved mapping
 * drafts survive a sub-tab switch.
 */
@Component({
  selector: 'app-company-location',
  standalone: true,
  imports: [
    CommonModule,
    SimpleDropdownComponent,
    EmptyResultComponent,
    LocationLanesComponent,
    LocationTunnelComponent,
    LocationWayComponent,
  ],
  templateUrl: './company-location.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./company-location.component.scss'],
})
export class CompanyLocationComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CompanyContextService);
  private readonly locationSettingsService = inject(LocationSettingsService);
  private readonly messageService = inject(NotificationService);
  private readonly subscriptions = new Subscription();
  private locationsSub?: Subscription;

  readonly tabs = LOCATION_SETTINGS_TABS;

  companyId = '';
  locationOptions: DropdownOption[] = [];
  locationsLoading = false;
  isLocationDropdownOpen = false;
  selectedLocationId = '';
  selectedLocation: AdminLocationRow | null = null;
  activeTab: LocationSettingsTab = 'lanes';

  /** Badge counts; null until the panel has loaded for the selected location. */
  activeLaneCount: number | null = null;
  activeTunnelPackageCount: number | null = null;

  private locationsById = new Map<string, AdminLocationRow>();
  /** Location asked for by the URL, applied once the location list is in. */
  private requestedLocationId = '';

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.queryParamMap.subscribe(params => {
        this.activeTab = parseLocationSettingsTab(params.get('tab'));
        this.requestedLocationId = params.get('location') || '';
        if (this.requestedLocationId !== this.selectedLocationId) {
          this.applyRequestedLocation();
        }
      })
    );
    this.subscriptions.add(
      this.context.current$.subscribe(company => {
        const nextCompanyId = company?.id || '';
        if (nextCompanyId === this.companyId) {
          return;
        }
        this.companyId = nextCompanyId;
        this.resetLocations();
        if (nextCompanyId) {
          this.loadLocations(nextCompanyId);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.locationsSub?.unsubscribe();
  }

  get hasLocations(): boolean {
    return this.locationOptions.length > 0;
  }

  tabCount(tab: LocationSettingsTab): number | null {
    if (tab === 'lanes') {
      return this.activeLaneCount;
    }
    if (tab === 'tunnel') {
      return this.activeTunnelPackageCount;
    }
    return null;
  }

  tabCountLabel(tab: LocationSettingsTab): string {
    if (tab === 'lanes') {
      return 'active lanes';
    }
    return tab === 'tunnel' ? 'active tunnel wash packages' : '';
  }

  toggleLocationDropdown(): void {
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
  }

  closeLocationDropdown(): void {
    this.isLocationDropdownOpen = false;
  }

  onLocationPicked(locationId: string): void {
    this.isLocationDropdownOpen = false;
    this.selectLocation(locationId);
    this.syncQueryParams();
  }

  selectTab(tab: LocationSettingsTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.syncQueryParams();
  }

  onLaneCountChange(count: number): void {
    this.activeLaneCount = count;
  }

  onTunnelPackageCountChange(count: number): void {
    this.activeTunnelPackageCount = count;
  }

  /** The Way tab saved the vendor location id: keep the cached row in step. */
  onLocationUpdated(location: AdminLocationRow): void {
    const existing = this.locationsById.get(location.id);
    const merged = { ...existing, ...location };
    this.locationsById.set(location.id, merged);
    if (this.selectedLocationId === location.id) {
      this.selectedLocation = merged;
    }
  }

  private resetLocations(): void {
    this.locationsSub?.unsubscribe();
    this.locationOptions = [];
    this.locationsById.clear();
    this.selectLocation('');
  }

  private selectLocation(locationId: string): void {
    const location = locationId ? this.locationsById.get(locationId) ?? null : null;
    const nextId = location?.id ?? '';
    if (nextId !== this.selectedLocationId) {
      this.activeLaneCount = null;
      this.activeTunnelPackageCount = null;
    }
    this.selectedLocationId = nextId;
    this.selectedLocation = location;
  }

  private applyRequestedLocation(): void {
    if (!this.locationsById.size) {
      return;
    }
    if (this.requestedLocationId && this.locationsById.has(this.requestedLocationId)) {
      this.selectLocation(this.requestedLocationId);
      return;
    }
    // Nothing (valid) asked for: a company with one location needs no pick.
    if (!this.requestedLocationId && this.locationOptions.length === 1) {
      this.selectLocation(this.locationOptions[0].value);
      this.syncQueryParams();
      return;
    }
    this.selectLocation('');
  }

  private loadLocations(companyId: string): void {
    this.locationsLoading = true;
    this.locationsSub = this.locationSettingsService.listLocations(companyId).subscribe({
      next: response => {
        if (companyId !== this.companyId) {
          return;
        }
        this.locationsLoading = false;
        const locations = response.status && Array.isArray(response.data) ? response.data : [];
        this.locationsById = new Map(locations.map(location => [location.id, location] as const));
        this.locationOptions = locations.map(location => ({
          label: location.branch || location.name || location.id,
          value: location.id,
        }));
        this.applyRequestedLocation();
      },
      error: () => {
        if (companyId !== this.companyId) {
          return;
        }
        this.locationsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load locations',
        });
      },
    });
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildLocationSettingsQueryParams(this.selectedLocationId, this.activeTab),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
